import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { api } from '../lib/api.js';
import { buildTenantHref } from '../lib/tenantPath.jsx';

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const code = params.get('fac') || params.get('code') || null;
  // Phase 8.4 — slug can come from the path (/t/:slug/login) or the query
  // (?tenant=<slug>, used when TenantRoot bounces an unauthed user here).
  // Path wins if both are present.
  const { slug: pathSlug } = useParams();
  const intendedSlug = pathSlug || params.get('tenant') || null;

  const [email, setEmail] = useState('nurse@demo.com');
  const [password, setPassword] = useState('demo1234');
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const [b, setB] = useState({
    display_name: 'Meds-to-Beds',
    theme_primary: '#1d4ed8',
    theme_accent:  '#0ea5e9',
    logo_url: null,
  });
  useEffect(() => {
    let alive = true;
    api.publicLanding(code)
      .then(r => { if (alive && r.landing) setB(prev => ({ ...prev, ...r.landing })); })
      .catch(() => {});
    return () => { alive = false; };
  }, [code]);

  async function submit(e) {
    e.preventDefault();
    setErr(null); setLoading(true);
    try {
      const r = await login(email, password);
      if (r?.must_change_password || r?.user?.must_change_password) {
        nav('/change-password', { replace: true });
      } else {
        // Phase 8.4 — prefer slug from JWT (user.tenant_slug), then ?tenant=
        // hint, otherwise fall back to legacy unprefixed route.
        const slug = r?.user?.tenant_slug || intendedSlug || null;
        nav(buildTenantHref(slug, '/'), { replace: true });
      }
    }
    catch (e) { setErr(e.message || 'login failed'); }
    finally { setLoading(false); }
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: `linear-gradient(135deg, ${b.theme_primary}0d 0%, ${b.theme_accent}1a 100%)` }}
    >
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {b.logo_url
              ? <img src={b.logo_url} alt="" className="h-8 w-8 rounded" onError={(e) => e.target.style.display='none'} />
              : <div className="h-8 w-8 rounded" style={{ background: b.theme_primary }} />
            }
            <span className="font-semibold text-slate-800">{b.display_name}</span>
          </Link>
          <nav className="flex gap-4 text-sm text-slate-600">
            <Link to="/help/faq" className="hover:text-slate-900">FAQ</Link>
            <Link to="/legal/privacy" className="hover:text-slate-900">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-slate-900">Terms</Link>
          </nav>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-lg shadow-sm border border-slate-200 p-6 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide font-semibold mb-1" style={{ color: b.theme_primary }}>
              Staff sign in
            </div>
            <h1 className="text-xl font-bold text-slate-900">{b.display_name}</h1>
            <p className="text-sm text-slate-500">Operational dashboard</p>
            {intendedSlug && (
              <p className="mt-1 text-xs text-slate-400">
                Tenant: <span className="font-mono">{intendedSlug}</span>
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Email</label>
            <input className="input" value={email} onChange={e => setEmail(e.target.value)} autoComplete="username" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Password</label>
            <input className="input" type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
          </div>
          {err && <div className="text-sm text-red-600">{err}</div>}
          <button className="btn-primary w-full" disabled={loading} style={{ background: b.theme_primary }}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
          <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
            Demo accounts: <br/>
            nurse@demo.com / pharm@demo.com / admin@demo.com / tech@demo.com (pw: demo1234)
          </div>
          <div className="text-xs text-center pt-1">
            <Link to="/" className="text-slate-500 hover:text-slate-800">← Back to home</Link>
          </div>
        </form>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500 justify-between">
          <div>© {new Date().getFullYear()} {b.display_name}. For demo / educational use.</div>
          <div className="flex gap-4">
            <Link to="/legal/terms"   className="hover:text-slate-700">Terms of Use</Link>
            <Link to="/legal/privacy" className="hover:text-slate-700">Privacy Notice</Link>
            <Link to="/help/faq"      className="hover:text-slate-700">FAQ &amp; Help</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
